import { AnimatePresence, motion } from "framer-motion";
import { useDebounce } from "usehooks-ts";
import { Stack, Loader, Divider, Pagination } from "components";
import { useGetDoctorsQuery } from "features/api";
import DoctorsList from "./DoctorsList";
import { useState } from "react";
import DoctorQrCodeModal from "components/Modal/DoctorQrCodeModal";
import { useNavigate } from "react-router-dom";

const CONSULT_LATER_REDIRECTION_DEEPLINK = "https://mwell.com.ph/deeplink?type=page&name=doctor_details&doctor_id=";

interface DoctorsProps {
  link: string;
  search?: string;
  filter: string;
  page: number;
  isPremium: boolean;
  onPageChange(page: number): void;
}

const Doctors: React.FC<DoctorsProps> = ({
  link,
  filter = "",
  search = "",
  page,
  isPremium,
  onPageChange,
}) => {
  const perPage = 10;
  const debouncedSearch = useDebounce(search);
  const doctorsQuery = useGetDoctorsQuery({
    page,
    perPage,
    search: debouncedSearch,
    filter,
    isPremium,
  });

  const { data, isLoading, isFetching } = doctorsQuery;
  const { data: doctors, count } = Object.assign({ data: [], count: 0 }, data);
  const totalPages = Math.ceil(count / perPage);
  const [doctorDetails, setDoctorDetails] = useState<any | null>(null);
  const [isShowDoctorQrModal, setisShowDoctorQrModal] = useState<boolean>(false);
  const navigate = useNavigate();

  const expandingLoader = (
    <motion.div
      key="loader"
      initial={{ height: 0 }}
      animate={{ height: "auto" }}
      exit={{ height: 0 }}
      style={{ placeSelf: "center" }}
      transition={{ duration: 0.1 }}
    >
      <Loader overlay={false} />
    </motion.div>
  );

  const list = (
    <>
      {isFetching && <Divider />}
      <DoctorsList 
        doctors={doctors} 
        link={link}
        onClick={(doctor) => {
          setDoctorDetails(doctor)
          if (!doctor.isPremium) {
            setisShowDoctorQrModal(true)
          } else {
            const linkTo = `/consult-later?doctor=${doctor.id}`;
            navigate(linkTo)
          }
        }} 
      />
    </>
  );

  const empty = !isFetching && "No doctor found";

  return isLoading ? (
    <Loader overlay={false} style={{ placeSelf: "center" }} />
  ) : (
    <Stack direction="column" gap={2}>
      <DoctorQrCodeModal
        qrValue={`${CONSULT_LATER_REDIRECTION_DEEPLINK}${doctorDetails?.id}`}
        title="QR CODE"
        onClose={() => setisShowDoctorQrModal(false)}
        isShow={isShowDoctorQrModal} 
        description="Booking this doctor is not yet available in this channel. Scan to book using your mWell mobile app. Consultation fees apply." 
      />
      <AnimatePresence>{isFetching && expandingLoader}</AnimatePresence>
      {doctors.length ? list : empty}
      {totalPages > 1 && (
        <>
          <Divider />
          <Pagination
            current={page}
            count={totalPages}
            onChange={onPageChange}
          />
        </>
      )}
    </Stack>
  );
};

export default Doctors;
